import React, { useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  setAccessoryProducts,
  setRepairCategories,
  setSubCategories,
  setLoading,
  selectAccessoryProducts,
  selectRepairCategories,
  selectSubCategories,
  selectIsLoading,
  addOrUpdateCartItem,
  selectCartItems,
} from "../../core/redux/accessoriesSlice";
import "./Accessories.css";
import { selectCartItems as selectPartItems } from "../../core/redux/partSlice";
import CategorySlider from "./CategorySlider";

const Accessories = ({
  selectedCategory,
  setSelectedCategory,
  selectedSubCategory,
  setSelectedSubCategory,
  currentView,
  setCurrentView,
}) => {
  const dispatch = useDispatch();
  const accessoryProducts = useSelector(selectAccessoryProducts);
  const repairCategories = useSelector(selectRepairCategories);
  const subCategories = useSelector(selectSubCategories);
  const isLoading = useSelector(selectIsLoading);
  const accessoryCartItems = useSelector(selectCartItems);
  const partCartItems = useSelector(selectPartItems);
  const allCartItems = [...accessoryCartItems, ...partCartItems];
  const { storeId } = useSelector((state) => state.user);

  const apiBase =
    process.env.REACT_APP_BASEURL ||
    window.__APP_CONFIG__?.API_BASE_URL || "";

  // CHANGE: Removed selectedCategory from dependency array
  // APIs are now called only once on mount or when storeId changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(setLoading(true));
        const BASE_URL = apiBase;

        // Fetch all data in parallel for better performance
        const [categoriesResponse, subCategoriesResponse, productsResponse] = 
          await Promise.all([
            axios.get(`${BASE_URL}api/v1/Product/GetCategories?storeId=${storeId}`),
            axios.get(`${BASE_URL}api/v1/Product/GetSubcategories?storeId=${storeId}`),
            axios.get(`${BASE_URL}api/v1/Product/GetProducts?storeId=${storeId}`)
          ]);

        // Transform categories data
        const transformedCategories = categoriesResponse.data.data.map(
          (item) => ({
            id: item.categoryId,
            name: item.categoryName,
            image: item.image
              ? `${BASE_URL}images/categories/${item.image}`
              : "assets/img/products/pos-product-10.svg",
          })
        );

        dispatch(setRepairCategories(transformedCategories));

        // Set Fast Food as default category only if no category is selected
        const fastFoodCategory = transformedCategories.find(
          cat => cat.name === "Fast Food"
        );
        if (fastFoodCategory && !selectedCategory) {
          setSelectedCategory(fastFoodCategory.id);
        }

        // Transform subcategories data
        const transformedSubCategories = subCategoriesResponse.data.data.map(
          (item) => ({
            id: item.subCategoryId,
            name: item.subCategoryName,
            categoryId: item.categoryId,
            image: item.image
              ? `${BASE_URL}images/subcategories/${item.image}`
              : "assets/img/products/pos-product-10.svg",
          })
        );

        dispatch(setSubCategories(transformedSubCategories));

        // Transform products data with all API fields
        const transformedProducts = productsResponse.data.data.map((item) => ({
          id: item.id,
          name: item.productName,
          sku: item.sku,
          category:
            transformedCategories.find((cat) => cat.id === item.categoryId)
              ?.name || "Uncategorized",
          categoryId: item.categoryId,
          subcategoryId: item.subcategoryId,
          price: item.price,
          image:
            item.imageList && item.imageList.length > 0
              ? `${BASE_URL}${item.imageList[0]}`
              : "assets/img/products/pos-product-02.svg",
          description: item.description,
          manufacturer: item.manufacturer,
          brandName: item.brandName,
          warrantyType: item.warrantyType,
          discountValue: item.discountValue,
          status: true,
        }));

        dispatch(setAccessoryProducts(transformedProducts));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        dispatch(setLoading(false));
      }
    };

    // Only fetch if we don't have data yet or storeId changes
    if (storeId) {
      fetchData();
    }
  }, [dispatch, storeId, apiBase]); // REMOVED selectedCategory from here

  // Handle category and subcategory clicks
  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentView("subcategories");
  };

  const handleSubCategoryClick = (subCategoryId) => {
    setSelectedSubCategory(subCategoryId);
    setCurrentView("products");
  };

  // Handle product click to add to cart
  const handleProductClick = (product) => {
    const existingItem = allCartItems.find((item) => item.id === product.id);
    const currentQuantity = existingItem ? existingItem.quantity : 0;

    dispatch(
      addOrUpdateCartItem({
        id: product.id,
        product: {
          name: product.name,
          price: product.price,
          image: product.image,
          category: product.category,
          categoryId: product.categoryId,
          subcategoryId: product.subcategoryId,
          description: product.description,
        },
        quantity: currentQuantity + 1,
      })
    );
  };

  // Filter products based on selected category and subcategory
  // This now happens purely client-side with no API calls
  const getFilteredProducts = () => {
    let filtered = accessoryProducts;

    if (selectedCategory) {
      filtered = filtered.filter(
        (product) => product.categoryId === selectedCategory
      );
    }

    if (selectedSubCategory) {
      filtered = filtered.filter(
        (product) => product.subcategoryId === selectedSubCategory
      );
    }

    return filtered;
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading data...</p>
      </div>
    );
  }

  // Show products view
  if (currentView === "products") {
    const filteredProducts = getFilteredProducts();
    const allCategories = repairCategories;
    if (filteredProducts.length === 0) {
      return (
        <div className="text-center py-4">
          <p>No products found for this selection</p>
        </div>
      );
    }

    return (
      <div className="products-container" style={{ padding: "1rem" }}>
        <div className="row mb-3">
          <div className="col-12">
            <CategorySlider
              categories={allCategories}
              selectedCategory={selectedCategory}
              onCategoryClick={(categoryId) => {
                setSelectedCategory(categoryId);
                setCurrentView("subcategories");
              }}
            />
          </div>
        </div>
        <div className="row g-3">
          {filteredProducts.map((product) => {
            const existingItem = allCartItems.find((item) => item.id === product.id);
            const quantity = existingItem ? existingItem.quantity : 0;

            return (
              <div
                key={product.id}
                className="col-sm-6 col-md-6 col-lg-6 col-xl-3 col-xxl-3"
              >
                <div
                  className="card border-1 rounded-4 p-3 d-flex flex-column justify-content-between h-100 product-card mb-1"
                  style={{
                    backgroundColor: "#fff",
                    borderLeft: "5px solid yellow",
                    cursor: "pointer",
                  }}
                  onClick={() => handleProductClick(product)}
                  tabIndex="0"
                >
                  <div className="text-center">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      {quantity > 0 && (
                        <span className="badge bg-primary px-2 py-1 position-absolute top-0 end-0 m-2">
                          {quantity}
                        </span>
                      )}
                      <span className="badge px-2 py-1 fs-12 mx-auto" style={{ backgroundColor: "#008080" }}>
                        &#x20B9;
                        {product.price ? product.price.toFixed(2) : "0.00"}
                      </span>
                    </div>

                    <text className=" text-dark mb-1 " style={{ fontfamily: 'Poppins, sans-serif', fontSize: "12px", fontWeight: "500" }}>{product.name}</text>
                    <span className="badge bg-light text-dark border px-2 py-1 text-uppercase mb-2 fs-12">
                      {product.brandName}
                    </span>

                    <small className="text-secondary d-block">
                      {product.manufacturer}
                      {product.warrantyType && ` • ${product.warrantyType}`}
                    </small>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Show subcategories view
  if (currentView === "subcategories" && selectedCategory) {
    const filteredSubCategories = subCategories.filter(
      (subCat) => subCat.categoryId === selectedCategory
    );
    const allCategories = repairCategories;
    if (filteredSubCategories.length === 0) {
      return (
        <div className="text-center py-4">
          <p>No subcategories found for this category</p>
        </div>
      );
    }

    return (
      <div className="products-container " style={{ padding: "1rem" }}>
        <div className="row mb-3">
          <div className="col-12">
            <CategorySlider
              categories={allCategories}
              selectedCategory={selectedCategory}
              onCategoryClick={(categoryId) => {
                setSelectedCategory(categoryId);
                setCurrentView("subcategories");
              }}
            />
          </div>
        </div>

        {filteredSubCategories.length === 0 ? (
          <div className="text-center py-4">
            <p>No subcategories found for this category</p>
          </div>
        ) : (
          <div className="row g-3">
            {filteredSubCategories.map((subCategory) => (
              <div
                key={subCategory.id}
                className="col-sm-6 col-md-6 col-lg-6 col-xl-3 col-xxl-3"
              >
                <div
                  className="card h-100 text-center subcategory-card"
                  onClick={() => handleSubCategoryClick(subCategory.id)}
                  style={{
                    cursor: "pointer",
                    borderRadius: "12px",
                    minHeight: "75px",
                    maxWidth: "150px",
                    margin: "0 auto",
                    border: "2px solid #e3f2fd",
                    transition: "all 0.3s ease",
                    marginTop: "15px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.1)";
                    e.currentTarget.style.borderColor = "#90caf9";
                    e.currentTarget.style.marginTop = "10px";
                    e.currentTarget.style.marginBottom = "5px";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "#e3f2fd";
                    e.currentTarget.style.marginTop = "15px";
                    e.currentTarget.style.marginBottom = "0";
                  }}
                >
                  <div className="card-body d-flex flex-column align-items-center justify-content-center p-3">
                    <div className="text-primary mb-2">
                      {/* {getSubCategoryIcon(subCategory.name)} */}
                    </div>
                    <h6 className="fw-bold mb-0 text-capitalize text-dark">
                      {subCategory.name}
                    </h6>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Show categories by default
  return (
    <div className="products-container" style={{ padding: "1rem" }}>
      <div className="row g-3">
        {repairCategories.map((category) => (
          <div
            key={category.id}
            className="col-sm-6 col-md-6 col-lg-6 col-xl-4 col-xxl-3"
          >
            <div
              className="card h-100 text-center category-card"
              onClick={() => handleCategoryClick(category.id)}
              style={{
                cursor: "pointer",
                borderRadius: "12px",
                minHeight: "100px",
                border: "2px solid #bbdefb",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.1)";
                e.currentTarget.style.borderColor = "#64b5f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#bbdefb";
              }}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center p-3">
                <div className="text-primary mb-2">
                  {/* {getCategoryIcon(category.name)} */}
                </div>
                <h5 className="fw-bold mb-0 text-capitalize text-dark">
                  {category.name}
                </h5>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

Accessories.propTypes = {
  selectedCategory: PropTypes.string,
  setSelectedCategory: PropTypes.func.isRequired,
  selectedSubCategory: PropTypes.string,
  setSelectedSubCategory: PropTypes.func.isRequired,
  currentView: PropTypes.string.isRequired,
  setCurrentView: PropTypes.func.isRequired,
};

export default Accessories;